/**
 * US household net worth percentile lookup and state-level (local) percentile estimation.
 * US data: Federal Reserve Survey of Consumer Finances 2022 (2022 dollars).
 * State medians: Census/SIPP and published estimates (approximate, for local ranking).
 */

// 2022 SCF: net worth at given percentile (bottom percentile, e.g. 75 = 75th percentile).
// Source: Federal Reserve SCF 2022. Note: 10th percentile is ~$1 (many households at or below zero).
const US_NET_WORTH_BY_PERCENTILE = [
  { percentile: 10, netWorth: 1 },
  { percentile: 25, netWorth: 20856 },
  { percentile: 50, netWorth: 162350 },
  { percentile: 75, netWorth: 553100 },
  { percentile: 90, netWorth: 1559240 },
  { percentile: 99, netWorth: 11640000 }
];

const US_MEDIAN_NET_WORTH = US_NET_WORTH_BY_PERCENTILE.find((p) => p.percentile === 50)?.netWorth ?? 162350;

/**
 * Normalize state string for lookup (lowercase, trim, handle common variants).
 * @param {string} state - State name or abbreviation from onboarding (e.g. "California", "CA")
 * @returns {string} Normalized key
 */
function normalizeStateKey(state) {
  if (!state || typeof state !== 'string') return '';
  const s = state.trim().toLowerCase();
  if (!s) return '';
  // Map common abbreviations and names to a canonical key
  const abbrToName = {
    al: 'alabama', ak: 'alaska', az: 'arizona', ar: 'arkansas', ca: 'california',
    co: 'colorado', ct: 'connecticut', de: 'delaware', fl: 'florida', ga: 'georgia',
    hi: 'hawaii', id: 'idaho', il: 'illinois', in: 'indiana', ia: 'iowa',
    ks: 'kansas', ky: 'kentucky', la: 'louisiana', me: 'maine', md: 'maryland',
    ma: 'massachusetts', mi: 'michigan', mn: 'minnesota', ms: 'mississippi',
    mo: 'missouri', mt: 'montana', ne: 'nebraska', nv: 'nevada', nh: 'new hampshire',
    nj: 'new jersey', nm: 'new mexico', ny: 'new york', nc: 'north carolina',
    nd: 'north dakota', oh: 'ohio', ok: 'oklahoma', or: 'oregon', pa: 'pennsylvania',
    ri: 'rhode island', sc: 'south carolina', sd: 'south dakota', tn: 'tennessee',
    tx: 'texas', ut: 'utah', vt: 'vermont', va: 'virginia', wa: 'washington',
    wv: 'west virginia', wi: 'wisconsin', wy: 'wyoming', dc: 'district of columbia'
  };
  if (s.length <= 3 && abbrToName[s]) return abbrToName[s];
  return s;
}

/**
 * State median household net worth (approximate 2022–2023, in dollars).
 * Sources: Census SIPP, SmartAsset/other published state wealth studies.
 * Used to approximate "top x% (locally)" by scaling US distribution to state.
 */
const STATE_MEDIAN_NET_WORTH = {
  alabama: 119000,
  alaska: 148000,
  arizona: 156000,
  arkansas: 99400,
  california: 249000,
  colorado: 247000,
  connecticut: 318000,
  delaware: 178000,
  'district of columbia': 258000,
  florida: 182000,
  georgia: 152000,
  hawaii: 503000,
  idaho: 189000,
  illinois: 186000,
  indiana: 157000,
  iowa: 178000,
  kansas: 158000,
  kentucky: 118000,
  louisiana: 76500,
  maine: 327000,
  maryland: 324000,
  massachusetts: 309000,
  michigan: 154000,
  minnesota: 228000,
  mississippi: 78000,
  missouri: 141000,
  montana: 196000,
  nebraska: 178000,
  nevada: 158000,
  'new hampshire': 287000,
  'new jersey': 298000,
  'new mexico': 112000,
  'new york': 206000,
  'north carolina': 167000,
  'north dakota': 176000,
  ohio: 142000,
  oklahoma: 114000,
  oregon: 219000,
  pennsylvania: 168000,
  'rhode island': 231000,
  'south carolina': 142000,
  'south dakota': 183000,
  tennessee: 142000,
  texas: 168000,
  utah: 208000,
  vermont: 251000,
  virginia: 239000,
  washington: 399000,
  'west virginia': 98000,
  wisconsin: 198000,
  wyoming: 218000
};

/**
 * Interpolate bottom percentile (0–100) from ordered brackets.
 * @param {number} netWorth - Household net worth in dollars
 * @param {{ percentile: number, netWorth: number }[]} brackets - Ascending by percentile
 * @returns {number} Bottom percentile (e.g. 75 = 75th percentile)
 */
function interpolatePercentile(netWorth, brackets) {
  if (netWorth <= brackets[0].netWorth) return brackets[0].percentile;
  if (netWorth >= brackets[brackets.length - 1].netWorth) return brackets[brackets.length - 1].percentile;
  for (let i = 0; i < brackets.length - 1; i++) {
    const lo = brackets[i];
    const hi = brackets[i + 1];
    if (netWorth >= lo.netWorth && netWorth <= hi.netWorth) {
      const t = (netWorth - lo.netWorth) / (hi.netWorth - lo.netWorth);
      return lo.percentile + t * (hi.percentile - lo.percentile);
    }
  }
  return 50;
}

/** Convert bottom percentile to "top x%" and clamp to 1–99. */
function toTopPercent(bottomPercentile) {
  const top = Math.round(100 - bottomPercentile);
  return Math.max(1, Math.min(99, top));
}

/**
 * Get "top x%" for US households (e.g. 25 = top 25%).
 * @param {number} netWorth - Current household net worth in dollars
 * @returns {number} Top percent (1–99), or 0 if net worth is zero/negative
 */
export function getTopPercentUS(netWorth) {
  const n = Number(netWorth);
  if (!isFinite(n) || n <= 0) return 0;
  return toTopPercent(interpolatePercentile(n, US_NET_WORTH_BY_PERCENTILE));
}

/**
 * Get "top x% (locally)" using state from onboarding.
 * Scales the US distribution by state median net worth for a state-relative rank.
 * City is accepted for API compatibility but not used (no city-level data).
 * @param {number} netWorth - Current household net worth in dollars
 * @param {string} [state] - State from onboarding (state), e.g. "California" or "CA"
 * @param {string} [city] - City from onboarding (city); unused, for future use
 * @returns {number|null} Top percent for state (1–99), or null if state unknown/missing
 */
export function getTopPercentLocal(netWorth, state, city) {
  const n = Number(netWorth);
  if (!isFinite(n) || n <= 0) return null;
  const stateKey = normalizeStateKey(state);
  if (!stateKey) return null;
  const stateMedian = STATE_MEDIAN_NET_WORTH[stateKey];
  if (stateMedian == null) return null;
  const scale = stateMedian / US_MEDIAN_NET_WORTH;
  const stateBrackets = US_NET_WORTH_BY_PERCENTILE.map(({ percentile, netWorth }) => ({
    percentile,
    netWorth: netWorth * scale
  }));
  return toTopPercent(interpolatePercentile(n, stateBrackets));
}
